import { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useFormik } from 'formik';
import { CompanyApi } from '../../../../../api/companyApi';
import {
  errorToast,
  successToast,
  getLocalStorageItem,
  isCopanySubAdmin
} from '../../../../../utils/helper';
import Loader from '../../../../../component/common/Loader';
import Topbar from '../../../../PulseSurvey/Topbar';
import Breadcrumb from '../../../../../component/common/Breadcrumb';
import { ROI_REPORT_FIELDS } from '../components/roiReportFields';
import AddEditRoiReportValidation from '../../../../../validation/AddEditRoiReportValidation';
import { Icons } from '../../../../../component/common/svgs';
import { StandardButton, StandardInput } from '../../../../../component/common/StandardUI';

const CloseIcon = Icons.close;

const INITIAL_VALUES = ROI_REPORT_FIELDS.reduce((acc, field) => {
  acc[field.key] = '';
  return acc;
}, {});

const HR_INTEGRATION_PROVIDERS = ['BambooHR', 'Workday', 'HiBob', 'Sage'];

const SUMMARY_FIELDS = [
  { key: 'netROI', label: 'Net ROI', suffix: '%' },
  { key: 'totalSavings', label: 'Total Savings', prefix: '£' },
  { key: 'roiPerPoundInvested', label: 'ROI per £1 Invested', prefix: '£' }
];

const titleCase = (key) =>
  key
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (c) => c.toUpperCase())
    .trim();

/**
 * The API returns `insights` as a single AI-generated string (RoiReport.aiInsights
 * is a String in the schema), while the report renders it as a bullet list. Coerce
 * to an array so a string payload cannot blow up on .map, splitting on newlines and
 * dropping any leading bullet the model wrote itself.
 */
const toInsightList = (value) => {
  if (Array.isArray(value)) return value;
  if (typeof value !== 'string') return [];
  return value
    .split('\n')
    .map((line) => line.replace(/^\s*[-•*]\s*/, '').trim())
    .filter(Boolean);
};

const RoiReport = () => {
  const userData = JSON.parse(getLocalStorageItem('userData'));
  const slug = userData?.slug;
  const isSubAdmin = isCopanySubAdmin();
  const pages = [
    { name: 'People plan', href: `/${slug}/people-plan`, current: false },
    { name: 'ROI Report', href: `/${slug}/people-plan/roi-report`, current: true }
  ];

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [report, setReport] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [solutions, setSolutions] = useState([]);
  const [insights, setInsights] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  // 'choose' shows the Integration-vs-Manual picker, before the user commits to an entry method.
  const [entryMode, setEntryMode] = useState('choose');
  const [connectedProvider, setConnectedProvider] = useState(null);
  const [showProviderPicker, setShowProviderPicker] = useState(false);

  const formik = useFormik({
    initialValues: INITIAL_VALUES,
    validationSchema: AddEditRoiReportValidation,
    validateOnBlur: false,
    onSubmit: () => { }
  });

  const fetchReport = async () => {
    try {
      setLoading(true);
      const response = await CompanyApi.fetchRoiReport();
      const payload = response?.data?.data;
      if (response?.data?.meta?.code === 1 && payload) {
        // API nests the raw figures under `report` and derived stats under `metrics`;
        // fall back to a flat payload in case an older report shape is returned.
        setReport(payload.report || payload);
        setMetrics(payload.metrics || null);
        setSolutions(Array.isArray(payload.solutions) ? payload.solutions : []);
        setInsights(toInsightList(payload.insights));
        setIsEditing(false);
      } else {
        setReport(null);
        setMetrics(null);
        setSolutions([]);
        setInsights([]);
        setIsEditing(!isSubAdmin);
        setEntryMode('choose');
      }
    } catch (error) {
      setReport(null);
      setMetrics(null);
      setSolutions([]);
      setInsights([]);
      setIsEditing(!isSubAdmin);
      setEntryMode('choose');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startEditing = () => {
    if (report) {
      const values = ROI_REPORT_FIELDS.reduce((acc, field) => {
        acc[field.key] = report[field.key] ?? '';
        return acc;
      }, {});
      formik.setValues(values);
    }
    // Updating existing figures goes straight to the manual form; the picker is only for first-time setup.
    setEntryMode('manual');
    setIsEditing(true);
  };

  const handleSubmit = async () => {
    const errors = await formik.validateForm();
    if (Object.keys(errors).length > 0) {
      formik.setTouched(
        ROI_REPORT_FIELDS.reduce((acc, field) => ({ ...acc, [field.key]: true }), {})
      );
      return;
    }

    const payload = ROI_REPORT_FIELDS.reduce((acc, field) => {
      acc[field.key] = Number(formik.values[field.key]);
      return acc;
    }, {});

    try {
      setSubmitting(true);
      const response = await CompanyApi.createRoiReport(payload);
      if (response?.data?.meta?.code === 1) {
        successToast(response?.data?.meta?.message || 'ROI report saved successfully');
        fetchReport();
      } else {
        errorToast(response?.data?.meta?.message || 'Failed to save ROI report');
      }
    } catch (error) {
      errorToast(error.message || 'Failed to save ROI report');
    } finally {
      setSubmitting(false);
    }
  };

  const groupedFields = useMemo(() => {
    return ROI_REPORT_FIELDS.reduce((acc, field) => {
      acc[field.group] = acc[field.group] || [];
      acc[field.group].push(field);
      return acc;
    }, {});
  }, []);

  const knownSummaryFields = metrics
    ? SUMMARY_FIELDS.filter(
      (field) => metrics[field.key] !== undefined && metrics[field.key] !== null
    )
    : [];
  const extraSummaryFields = metrics
    ? Object.keys(metrics)
      .filter((key) => !SUMMARY_FIELDS.some((field) => field.key === key))
      .map((key) => ({ key, label: titleCase(key) }))
    : [];
  const availableSummaryFields = [...knownSummaryFields, ...extraSummaryFields];

  return (
    <div className="relative lg:mb-[45px]">
      {(loading || submitting) && <Loader />}
      <Helmet>
        <meta charSet="utf-8" />
        <title>People plan - ROI Report | Shoorah Admin</title>
      </Helmet>
      <Topbar title="People plan" infoBtn={false} />
      <Breadcrumb pageList={pages} className="lg:mt-8" />

      <div className="mt-6">
        {!isEditing && !report && isSubAdmin && (
          <div className="rounded-input border-[1.5px] border-shoorah-borderGray bg-white dark:bg-shoorah-darkBgTabColor dark:border-shoorah-darkBorder p-10 text-center text-sm text-gray-400">
            No ROI report has been generated yet.
          </div>
        )}

        {!isEditing && report && (
          <>
            {!isSubAdmin && (
              <div className="flex justify-end mb-6">
                <StandardButton text="Update Figures" onClick={startEditing} />
              </div>
            )}

            {availableSummaryFields.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
                {availableSummaryFields.map((field) => (
                  <div
                    key={field.key}
                    className="rounded-input border-[1.5px] border-shoorah-borderGray bg-white dark:bg-shoorah-darkBgTabColor dark:border-shoorah-darkBorder p-4"
                  >
                    <p className="text-xs text-gray-400 mb-1">{field.label}</p>
                    <p className="text-lg font-semibold text-shoorah-primary">
                      {field.prefix || ''}
                      {metrics[field.key]}
                      {field.suffix || ''}
                    </p>
                  </div>
                ))}
              </div>
            )}

            {insights.length > 0 && (
              <div className="rounded-input border-[1.5px] border-shoorah-borderGray bg-white dark:bg-shoorah-darkBgTabColor dark:border-shoorah-darkBorder p-6 mb-6">
                <h3 className="text-base font-medium text-gray-700 dark:text-gray-200 mb-3">
                  Insights
                </h3>
                <ul className="list-disc pl-5 space-y-1 text-sm text-gray-700 dark:text-gray-200">
                  {insights.map((insight, index) => (
                    <li key={index}>{typeof insight === 'string' ? insight : insight?.text}</li>
                  ))}
                </ul>
              </div>
            )}

            {solutions.length > 0 && (
              <div className="mb-6">
                <h3 className="text-base font-medium text-gray-700 dark:text-gray-200 mb-3">
                  Recommended Solutions
                </h3>
                <div className="flex gap-4 overflow-x-auto pb-2">
                  {solutions.map((solution, index) => (
                    <div
                      key={index}
                      className="min-w-[240px] max-w-[280px] shrink-0 rounded-input border-[1.5px] border-shoorah-borderGray bg-white dark:bg-shoorah-darkBgTabColor dark:border-shoorah-darkBorder p-4"
                    >
                      <p className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                        {solution?.title || `Solution ${index + 1}`}
                      </p>
                      <p className="text-xs text-gray-400">{solution?.description || solution}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-4">
              {Object.entries(groupedFields).map(([group, fields]) => (
                <div
                  key={group}
                  className="rounded-input border-[1.5px] border-shoorah-borderGray bg-white dark:bg-shoorah-darkBgTabColor dark:border-shoorah-darkBorder p-6"
                >
                  <h3 className="text-base font-medium text-gray-700 dark:text-gray-200 mb-3">
                    {group}
                  </h3>
                  <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                    {fields.map((field) => (
                      <div key={field.key}>
                        <dt className="text-gray-400">
                          {field.label}
                          {field.hint && (
                            <span className="block text-xs text-gray-400">({field.hint})</span>
                          )}
                        </dt>
                        <dd className="text-gray-700 dark:text-gray-200">
                          {field.prefix || ''}
                          {report[field.key] ?? '—'}
                          {field.suffix || ''}
                        </dd>
                      </div>
                    ))}
                  </dl>
                </div>
              ))}
            </div>
          </>
        )}

        {isEditing && entryMode !== 'manual' && (
          <div className="rounded-input border-[1.5px] border-shoorah-borderGray bg-white dark:bg-shoorah-darkBgTabColor dark:border-shoorah-darkBorder sm:p-6 p-4">
            <h2 className="text-lg font-medium text-gray-700 dark:text-gray-200">Integration</h2>
            <p className="text-sm text-gray-400 mt-1">
              Automatically connect your HR system to get the most accurate insights in Shoorah.
            </p>
            <p className="text-sm text-gray-400 mt-1">
              We integrate with BambooHR, Workday, HiBob, and Sage for smooth data syncing.
            </p>

            {entryMode === 'integration' &&
              (connectedProvider ? (
                <div className="mt-4">
                  <p className="text-sm text-gray-700 dark:text-gray-200 mb-2">
                    Integration successful
                  </p>
                  <div className="inline-flex items-center gap-2 rounded-lg border-[1.5px] border-shoorah-borderGray bg-shoorah-inputBackground dark:bg-shoorah-darkBgTabColor px-4 py-2 text-sm text-gray-700 dark:text-gray-200">
                    {connectedProvider}
                    <button
                      type="button"
                      onClick={() => setConnectedProvider(null)}
                      aria-label="Remove integration"
                      className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    >
                      <CloseIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ) : (
                showProviderPicker && (
                  <div className="mt-4 flex flex-wrap gap-2">
                    {HR_INTEGRATION_PROVIDERS.map((provider) => (
                      <button
                        key={provider}
                        type="button"
                        onClick={() => setConnectedProvider(provider)}
                        className="rounded-full border-[1.5px] border-shoorah-borderGray px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:border-shoorah-btn dark:hover:border-shoorah-btnDark"
                      >
                        {provider}
                      </button>
                    ))}
                  </div>
                )
              ))}

            <div className="flex flex-wrap gap-3 mt-6">
              <StandardButton
                text="Add Integration"
                onClick={() => {
                  setEntryMode('integration');
                  setShowProviderPicker(true);
                }}
              />
              <StandardButton
                text="Add Data Manually"
                variant="secondary"
                onClick={() => setEntryMode('manual')}
              />
            </div>
          </div>
        )}

        {isEditing && entryMode === 'manual' && (
          <form
            onSubmit={(e) => e.preventDefault()}
            className="rounded-input border-[1.5px] border-shoorah-borderGray bg-white dark:bg-shoorah-darkBgTabColor dark:border-shoorah-darkBorder sm:p-6 p-4 space-y-6"
          >
            <div>
              <h2 className="text-lg font-medium text-gray-700 dark:text-gray-200">
                Manual Data Entry
              </h2>
              <p className="text-sm text-gray-400 mt-1">
                Automatically connect your HR system to get the most accurate insights in Shoorah.
              </p>
            </div>

            {Object.entries(groupedFields).map(([group, fields]) => (
              <div key={group}>
                <h3 className="text-base font-medium text-gray-700 dark:text-gray-200 mb-3">
                  {group}:
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  {fields.map((field) => (
                    <StandardInput
                      key={field.key}
                      id={field.key}
                      name={field.key}
                      type="number"
                      label={`${field.label}${field.prefix ? ` (${field.prefix})` : ''}${field.suffix ? ` (${field.suffix})` : ''
                        }${field.hint ? ` (${field.hint})` : ''}`}
                      placeholder={field.placeholder}
                      value={formik.values[field.key]}
                      onChange={formik.handleChange}
                      error={formik.touched[field.key] && formik.errors[field.key]}
                      isRequired
                    />
                  ))}
                </div>
              </div>
            ))}

            <div className="flex justify-end gap-3 pt-2">
              {report ? (
                <StandardButton
                  text="Cancel"
                  variant="secondary"
                  onClick={() => setIsEditing(false)}
                />
              ) : (
                <StandardButton
                  text="Back"
                  variant="secondary"
                  onClick={() => setEntryMode('choose')}
                />
              )}
              <StandardButton
                text={report ? 'Save Changes' : 'Save'}
                disabled={submitting}
                onClick={handleSubmit}
              />
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default RoiReport;
